import { ComponentType } from "@angular/cdk/portal";
import { Component, inject, OnInit } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Subject, takeUntil } from "rxjs";

import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { DeauthorizeSessionsComponent } from "../deauthorize-sessions.component";
import { DeleteAccountComponent } from "../delete-account.component";
import { PurgeVaultComponent } from "../purge-vault.component";
import { RemoveBasiqConsentComponent } from "../remove-basiq-consent/remove-basiq-consent.component";
import { RemoveFiskilConsentComponent } from "../remove-fiskil-consent/remove-fiskil-consent.component";
import { ScopeValue } from "@bitwarden/web-vault/app/models/data/role-access.data";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { ServerVersionConfigType } from "@bitwarden/web-vault/app/models/types/environement-config.type";
import { DataRepositoryService } from "@bitwarden/web-vault/app/services/DataRepository/data-repository.service";
import { FormControl } from "@angular/forms";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { basiqIntegrationEnabled } from "@bitwarden/web-vault/utils/flags";

@Component({
  selector: "app-advanced-features",
  templateUrl: "advanced-features.component.html",
})
export class AdvancedFeaturesComponent implements OnInit {
  private userStoreService = inject(UserStoreService);
  private dialogRef: MatDialogRef<any> | null = null;
  private destroy$ = new Subject<void>();
  private platformUtilsService: PlatformUtilsService = inject(PlatformUtilsService);
  private dataRepositoryService = inject(DataRepositoryService);
  protected version: string;
  protected serverVersion = "-";
  protected formControl = new FormControl(
    JSON.stringify(this.userStoreService.plugin.collection(), null, 2),
  );
  contextList: {
    roleScope: ScopeValue;
    buttonText: string;
    description: string;
    separator?: boolean;
    buttonOptions: ActionButton;
  }[] = [];

  constructor(
    private dialog: MatDialog,
    private i18nService: I18nService,
  ) {
    const items: typeof this.contextList = [
      {
        roleScope: "GLOSS",
        buttonText: "deauthorizeSessions",
        description: "deauthorizeSessionsDesc",
        buttonOptions: this.generateButtonOptions(
          "deauthorizeAction",
          DeauthorizeSessionsComponent,
          "deauthorizeSessions",
        ),
      },
      {
        roleScope: "BETA_ACCESS",
        buttonText: "purgeVault",
        description: "purgeVaultDesc",
        buttonOptions: this.generateButtonOptions("purgeAction", PurgeVaultComponent, "purgeVault"),
      },
      {
        roleScope: "GLOSS",
        buttonText: "removeBasiqConsent",
        description: "removeBasiqConsentDesc",
        buttonOptions: this.generateButtonOptions(
          "remove",
          RemoveBasiqConsentComponent,
          "removeBasiqConsent",
        ),
      },
      {
        roleScope: "GLOSS",
        buttonText: "removeFiskilConsent",
        description: "removeFiskilConsentDesc",
        buttonOptions: this.generateButtonOptions(
          "remove",
          RemoveFiskilConsentComponent,
          "removeFiskilConsent",
        ),
      },
      {
        roleScope: "GLOSS",
        buttonText: "deleteAccount",
        description: "deleteAccountDesc",
        separator: true,
        buttonOptions: this.generateButtonOptions(
          "delete",
          DeleteAccountComponent,
          "deleteAccount",
        ),
      },
    ];
    this.contextList = items.filter(
      (item) => item.buttonText !== "removeBasiqConsent" || basiqIntegrationEnabled(),
    );
  }

  async ngOnInit() {
    this.version = await this.platformUtilsService.getApplicationVersion();
    try {
      const config = process.env.SERVER_VERSION as unknown as ServerVersionConfigType;
      const stage = config.apiStage ? `/${config.apiStage}` : "";
      const baseUrl = `${config.url}${stage}`;
      const data = (await this.dataRepositoryService.send(
        "GET",
        config.endpoint.version,
        null,
        true,
        true,
        baseUrl,
      )) as { version?: string };
      this.serverVersion = data?.version ?? "-";
    } catch {
      this.serverVersion = "-";
    }
  }

  // todo now we can really have a factory for this
  generateButtonOptions(
    labelKey: string,
    component: ComponentType<any>,
    rowKey: string,
  ): ActionButton {
    return new ActionButton({
      text: this.i18nService.t(labelKey),
      ariaLabel: this.i18nService.t(rowKey),
      fullClass:
        "tw-h-9 tw-w-auto tw-px-5 tw-rounded-xl tw-border-none tw-bg-color-status-error-solid tw-shadow-elevation-card tw-text-color-text-on-action md:tw-h-11",
      ghostDisabled: true,
      ghostShadow: true,
      onClick: () => this.openDialog(component),
      isEnabled: !this.isDialogOpen(),
    });
  }

  openDialog(component: ComponentType<any>) {
    this.dialogRef = this.dialog.open(component, {
      panelClass: "mobile-dialog",
      width: "600px",
      maxWidth: "100vh",
    });
    this.dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.dialogRef = null;
      });
  }

  isDialogOpen(): boolean {
    return this.dialogRef !== null;
  }

  savePlugin() {
    this.userStoreService.plugin.saveConfigs(this.formControl.value);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
